/**
 * Just-intonation core for Carnatic swaras.
 *
 * Everything is relative to a chosen Sa (shruti). Swara pitches are defined by
 * small-integer frequency ratios (just intonation), NOT 12-TET — this is the
 * whole point of the app (see docs/DESIGN.md §7, ADR-0004).
 */
/** The 12 Carnatic swara-position symbols (reference notation from the design). */
export const SWARA_SYMBOLS = [
    'S',
    'r',
    'R',
    'g',
    'G',
    'm',
    'M',
    'P',
    'd',
    'D',
    'n',
    'N',
];
/**
 * Just-intonation ratio of each swara relative to Sa (=1/1).
 * Source: the classical JI mapping used across Carnatic pitch tools.
 */
export const JI_RATIOS = {
    S: 1 / 1,
    r: 16 / 15, // Shuddha Rishabham / komal Re
    R: 9 / 8, // Chatushruti Rishabham
    g: 6 / 5, // Sadharana Gandharam / komal Ga
    G: 5 / 4, // Antara Gandharam
    m: 4 / 3, // Shuddha Madhyamam
    M: 45 / 32, // Prati Madhyamam
    P: 3 / 2, // Panchamam
    d: 8 / 5, // Shuddha Dhaivatham / komal Dha
    D: 5 / 3, // Chatushruti Dhaivatham
    n: 9 / 5, // Kaisiki Nishadham / komal Ni
    N: 15 / 8, // Kakali Nishadham
};
/** Human-readable long names, handy for UI labels. */
export const SWARA_NAMES = {
    S: 'Shadjam',
    r: 'Shuddha Rishabham',
    R: 'Chatushruti Rishabham',
    g: 'Sadharana Gandharam',
    G: 'Antara Gandharam',
    m: 'Shuddha Madhyamam',
    M: 'Prati Madhyamam',
    P: 'Panchamam',
    d: 'Shuddha Dhaivatham',
    D: 'Chatushruti Dhaivatham',
    n: 'Kaisiki Nishadham',
    N: 'Kakali Nishadham',
};
/** Cents between two frequencies (or ratios): 1200 * log2(a/b). */
export function cents(freq, reference) {
    return 1200 * Math.log2(freq / reference);
}
/** Absolute frequency (Hz) of a swara for a given Sa, in a given octave offset. */
export function swaraFrequency(saHz, symbol, octave = 0) {
    return saHz * JI_RATIOS[symbol] * 2 ** octave;
}
// --- Western pitch (for shruti selection: users set Sa as e.g. "G", "G#") ---
const NOTE_SEMITONES = {
    C: 0,
    'C#': 1,
    Db: 1,
    D: 2,
    'D#': 3,
    Eb: 3,
    E: 4,
    F: 5,
    'F#': 6,
    Gb: 6,
    G: 7,
    'G#': 8,
    Ab: 8,
    A: 9,
    'A#': 10,
    Bb: 10,
    B: 11,
};
/**
 * Frequency (Hz) of a Western note name like "G", "G#4", "A4".
 * Defaults to octave 4 when none is given (A4 = 440 Hz).
 * Practitioners typically set the tanpura/shruti this way (see AC-1.5).
 */
export function noteToFrequency(note) {
    const m = /^([A-Ga-g])([#b]?)(-?\d+)?$/.exec(note.trim());
    if (!m)
        throw new Error(`Invalid note name: "${note}"`);
    const [, letter, accidental, octaveStr] = m;
    const key = `${letter.toUpperCase()}${accidental ?? ''}`;
    const semitone = NOTE_SEMITONES[key];
    if (semitone === undefined)
        throw new Error(`Unknown note: "${note}"`);
    const octave = octaveStr === undefined ? 4 : Number(octaveStr);
    // MIDI note number, then equal-tempered frequency for the *reference tonic*.
    const midi = (octave + 1) * 12 + semitone;
    return 440 * 2 ** ((midi - 69) / 12);
}
//# sourceMappingURL=music.js.map